import { useEffect } from "react";

import type { AuthClient } from "@nav-games/auth-client";

import { SignIn } from "../ui/SignIn";

interface HomeProps {
  auth: AuthClient;
  onNavigate: (to: string) => void;
}

/// Public landing page. When signed out it pitches the app and shows
/// the sign-in card. When signed in it bounces straight to /play so
/// the user lands in the games hub on every return visit.
export function Home({ auth, onNavigate }: HomeProps) {
  const session = auth.session();

  useEffect(() => {
    if (session) onNavigate("/play");
  }, [session, onNavigate]);

  if (session) return null;

  return (
    <>
      <p className="muted" style={{ fontSize: 17 }}>
        Drill the seamanship skills every sailor is supposed to know
        — and most of us forget between certificates. Built for RYA
        Day Skipper / Yachtmaster candidates, Sea Cadets, and anyone
        who wants the basics back at their fingertips before the
        next passage.
      </p>

      <div className="card">
        <h2 style={{ marginTop: 0 }}>What's inside</h2>
        <h3 style={{ marginTop: 8 }}>Visual & audio signals</h3>
        <ul className="small muted">
          <li>
            <strong>Morse</strong> — ITU letters + digits, audio
            playback at 5–20 WPM, screen-flash + Aldis-lamp torch
            mode, mic-based live decode.
          </li>
          <li>
            <strong>Semaphore</strong> — 8-position arm code, A–Z
            + the J-prefix numerals.
          </li>
          <li>
            <strong>ICS flags</strong> — letter flags + numeric
            pennants, full operational meanings (Oscar = MOB, etc.),
            two-flag combinations (NC distress, AB tug urgent…).
          </li>
          <li>
            <strong>NATO phonetic</strong> — A–Z + 0–9 with
            audio-review mode (press-to-hear) and confusion drills.
          </li>
        </ul>

        <h3>Watch-keeping</h3>
        <ul className="small muted">
          <li>
            <strong>Nav lights</strong> — fourteen vessels × four
            aspects (head-on, stern, both beams), fog-signal audio
            playback, Rule 21 arcs-of-visibility diagram.
          </li>
          <li>
            <strong>Day shapes</strong> — IRPCS Rules 24–30 black-
            silhouette signals.
          </li>
          <li>
            <strong>IALA buoyage</strong> — cardinals, laterals,
            isolated-danger, safe-water, special, emergency-wreck;
            Region A / B switch.
          </li>
          <li>
            <strong>IRPCS rules + scenarios</strong> — 20 animated
            give-way situations covering Rules 9–18; rule reference
            for the look-up.
          </li>
        </ul>

        <h3>Navigation</h3>
        <ul className="small muted">
          <li>
            <strong>Chartwork</strong> — DR/EP/CTS, water/ground/
            tidal vectors, fix + waypoint symbols.
          </li>
          <li>
            <strong>Tides</strong> — heights diagram, rule of
            twelfths + sinusoidal calculators, secondary-port
            corrections, time-at-height with live curve.
          </li>
          <li>
            <strong>Pilotage</strong> — transits, leading lines,
            danger bearings (with a live NLT/NMT calculator),
            harbour-entry pre-flight, IPTS signals.
          </li>
          <li>
            <strong>GPS literacy</strong> — datums, COG/SOG/CTS,
            DOP, eight failure modes and their mitigations.
          </li>
        </ul>

        <h3>Safety</h3>
        <ul className="small muted">
          <li>
            <strong>Mayday + DSC</strong> — interactive script
            builder for the Channel-16 voice call.
          </li>
          <li>
            <strong>Distress signals</strong> — IRPCS Annex IV
            non-flag signals (flares, EPIRB, SART, dye, arms-wave).
          </li>
          <li>
            <strong>MOB recovery</strong> — six animated manoeuvres
            (figure-8, beam-reach return, Williamson, Anderson,
            Scharnow, quick-stop).
          </li>
          <li>
            <strong>Pre-departure check</strong> — 44-item
            interactive checklist with progress saved per device.
          </li>
        </ul>

        <h3>Seamanship</h3>
        <ul className="small muted">
          <li>
            <strong>Knots</strong> — the eight RYA knots with
            step-by-step tying.
          </li>
          <li>
            <strong>Anchoring</strong> — anchor types, holding
            ground, scope, kedge use cases.
          </li>
          <li>
            <strong>Met Office weather</strong> — shipping-forecast
            terminology, forward + reverse drills.
          </li>
        </ul>

        <h3>Games</h3>
        <p className="small muted" style={{ margin: "4px 0 0" }}>
          Flashcards with spaced repetition, beat-the-clock timed
          rounds, mixed-exam mode that draws from every module,
          and <strong>Soundings</strong> — a battleships ×
          minesweeper hybrid where you hunt a hidden fleet using
          IALA cardinal-mark clues. Two-player Bluetooth Hangman
          and Soundings for paired phones in range.
        </p>
      </div>

      <SignIn auth={auth} onSignedIn={onNavigate} />
    </>
  );
}
